<script lang="ts" setup>
    import { reactive } from 'vue';
    import { type Contact } from './Contact.access';
    import { type Timeline } from './Contact.access';
    import TimelineList from '@shared/components/List.vue';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { formatNumericText } from '@cwd/Shared/utils/formatNumericText';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import NavIcons from '@cwd/Navigation/NavIcons.vue';

    const props = defineProps<{
        contact: Contact;
        timeline: Timeline[];
        isBusy: boolean;
    }>();

    const allFilterOptions = () => {
        return {
            IssueRecipient: {
                key: 'IssueRecipient',
                name: $Resources.ItemTypeIssue,
            },
            SmsRecipient: {
                key: 'SmsRecipient',
                name: $Resources.ItemTypeSmsMessage,
            },
            SurveyResponse: {
                key: 'SurveyResponse',
                name: $Resources.ItemTypeSurvey,
            },
            EmailBounce: {
                key: 'EmailBounce',
                name: $Resources.ItemTypeBounce,
            },
            EmailUnbounce: {
                key: 'EmailUnbounced',
                name: $Resources.ItemTypeUnbounce,
            },
            SmsBounce: {
                key: 'SmsBounce',
                name: $Resources.ItemTypeBounceSms,
            },
            SmsUnbounce: {
                key: 'SmsUnbounced',
                name: $Resources.ItemTypeUnbounceSms,
            },
            Unsubscribe: {
                key: 'Unsubscribe',
                name: $Resources.ItemTypeUnsubscribed,
            },
            UnsubscribeSms: {
                key: 'SmsUnsubscribe',
                name: $Resources.ItemTypeUnsubscribedSms,
            },
            UnsubscribeCategory: {
                key: 'UnsubscribeCategory',
                name: $Resources.ItemTypeUnsubscribedCategory,
            },
            EventParticipant: {
                key: 'EventParticipant',
                name: $Resources.ItemTypeEvent,
            },
        };
    };

    type ItemTypes = 'IssueRecipient' | 'SmsRecipient' | 'SurveyResponse' | 'EmailBounce' | 'EmailUnbounced' | 'Unsubscribe' | 'UnsubscribeCategory' | 'EventParticipant' | 'SmsBounce' | 'SmsUnbounced' | 'SmsUnsubscribe';

    const state = reactive<{
        filter: null | ItemTypes;
    }>({
        filter: null,
    });

    const filterOptions = () => {
        return ['[Title]', '[Description]'];
    };

    const filteredTimeline = () => {
        if (!state.filter) return props.timeline;

        return props.timeline.filter((item: Timeline) => {
            return item.TypeName === state.filter;
        });
    };

    const orderOptions = () => {
        return [{ display: $Resources.DateLabel, key: 'OccuredAt' }];
    };

    //Maybe needed?
    /*
    $Resources.EventGuestListAcceptedLabel
    $Resources.EventGuestListAttendedLabel
    $Resources.EventGuestListCancelledLabel
    $Resources.EventGuestListDeclinedLabel
    $Resources.EventGuestListInvitedLabel
    $Resources.EventGuestListNoShowLabel
    $Resources.EventGuestListParticipantLabel
    $Resources.EventGuestListRegisteredLabel
    $Resources.EventGuestListWaitlistLabel
    */

    const getItemTypeName = (name: ItemTypes) => {
        switch (name) {
            case 'IssueRecipient':
                return $Resources.ItemTypeIssue;
            case 'SmsRecipient':
                return $Resources.ItemTypeSmsMessage;
            case 'SurveyResponse':
                return $Resources.ItemTypeSurvey;
            case 'EmailBounce':
                return $Resources.ItemTypeBounce;
            case 'EmailUnbounced':
                return $Resources.ItemTypeUnbounce;
            case 'SmsBounce':
                return $Resources.ItemTypeBounceSms;
            case 'SmsUnbounced':
                return $Resources.ItemTypeUnbounceSms;
            case 'Unsubscribe':
                return $Resources.ItemTypeUnsubscribed;
            case 'SmsUnsubscribe':
                return $Resources.ItemTypeUnsubscribedSms;
            case 'UnsubscribeCategory':
                return $Resources.ItemTypeUnsubscribedCategory;
            case 'EventParticipant':
                return $Resources.ItemTypeEvent;
        }
    };
</script>

<script lang="ts"></script>

<template>
    <h2 class="h4">{{ $Resources.ContactEventsHeading }}</h2>

    <div class="stat-highlights panel panel-default">
        <div class="row no-gutter d-flex flex-wrap">
            <div class="flex-col col-sm-4 col-xs-12 col-print-4">
                <div class="stat-highlights-body ug-bars">
                    <p class="bar-label">{{ $Resources.ContactNumberOfInteractionsLabel }}</p>
                    <p class="value">
                        {{ props.timeline.length }}
                    </p>
                    <div class="bar">
                        <span style="width: 100%" class="bar-value sent"></span>
                    </div>
                </div>
            </div>
            <div class="flex-col col-sm-4 col-xs-12 col-print-4">
                <div class="stat-highlights-body ug-bars">
                    <p class="bar-label">
                        {{ $Resources.IssuesOpenedPercentageLabel }}
                        <small>({{ $Resources.IssueTypeMailing }})</small>
                    </p>
                    <p class="value">
                        {{ formatNumericText(props.contact.OpenPercentage) + '%' }}
                    </p>
                    <div class="bar">
                        <span :style="'width:' + props.contact.OpenPercentage + '%'" class="bar-value bg-opened"></span>
                    </div>
                </div>
            </div>
            <div class="flex-col col-sm-4 col-xs-12 col-print-4">
                <div class="stat-highlights-body ug-bars">
                    <p class="bar-label">
                        {{ $Resources.IssuesClickedPercentageLabel }}
                        <small>({{ $Resources.IssueTypeMailing }})</small>
                    </p>
                    <p class="value">
                        {{ formatNumericText(props.contact.ClickPercentage) + '%' }}
                    </p>
                    <div class="bar">
                        <span :style="'width:' + props.contact.ClickPercentage + '%'" class="bar-value bg-clicked"></span>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <TimelineList :items="filteredTimeline()" :orderOptions="orderOptions()" :loading="props.isBusy" :filterKeys="filterOptions()" displayAs="timeline">
        <template #toolbar>
            <BSDropdownButton menuClass="dropdown-menu-right" :active="state.filter !== null" :closeOnClickInside="true">
                <template #toggle>
                    <span class="glyphicon glyphicon-filter m-xxs-r"></span>
                    {{ $Resources.FilterHeading }}
                </template>
                <template #content>
                    <li role="presentation" class="dropdown-header">{{ $Resources.FilterHeading }}:</li>
                    <li :class="state.filter === null && 'active'">
                        <a href="javascript:void(0)" @click="state.filter = null">
                            {{ $Resources.ShowAllLabel }}
                        </a>
                    </li>
                    <li v-for="{ key, name } in allFilterOptions()" :class="state.filter === key && 'active'">
                        <a href="javascript:void(0)" @click="(state.filter as any) = key">
                            {{ name }}
                        </a>
                    </li>
                </template>
            </BSDropdownButton>
        </template>
        <template #item="{ item, index }">
            <h2 v-if="index === 0 || (props.timeline[index - 1] && props.timeline[index - 1].OccuredAt.slice(5, 7) !== item.OccuredAt.slice(5, 7))"
                class="timeline-date-heading">
                <time :datetime="item.OccuredAt">
                    {{
                        formatDateTime(item.OccuredAt, {
                            month: 'long',
                            year: 'numeric',
                        })
                    }}
                </time>
            </h2>

            <div class="flex-1 m-md-t p-relative" style="min-width: min(400px, 100%)">
                <p class="d-flex flex-wrap align-items-center gap-sm timeline-bullet">
                    <strong v-show="item.TypeName !== 'ContactCreated'">
                        {{ getItemTypeName(item.TypeName) }}
                    </strong>
                    <time :datetime="item.OccuredAt" class="m-auto-r">{{ formatDateTime(item.OccuredAt) }}</time>

                    <span v-if="item.TypeName === 'IssueRecipient'" class="d-flex flex-wrap align-items-center gap-sm fw-600 timeline-stats">
                        <span v-show="item.RecipientCount > 0" class="d-flex align-items-center gap-xs">
                            <NavIcons icon="check" :size="14" :strokeWidth="3"></NavIcons>
                            {{ $Resources.ContactRecipientCountLabel }}
                        </span>
                        <span class="d-flex align-items-center gap-xs" :class="!item.OpenCount && 'o-40'">
                            <NavIcons :icon="item.OpenCount ? 'check' : 'close'" :size="14" :strokeWidth="3"></NavIcons>
                            {{ $Resources.ContactOpenedCountLabel + ' (' + formatNumericText(item.OpenCount) + ')' }}
                        </span>
                        <span class="d-flex align-items-center gap-xs" :class="!item.ClickCount && 'o-40'">
                            <NavIcons :icon="item.ClickCount ? 'check' : 'close'" s :size="14" :strokeWidth="3"></NavIcons>
                            {{ $Resources.ContactClickedCountLabel + ' (' + formatNumericText(item.ClickCount) + ')' }}
                        </span>
                    </span>

                    <span v-if="item.TypeName === 'SurveyResponse'"
                          class="d-flex flex-wrap align-items-center gap-xs fw-600 timeline-stats"
                          :class="!item.ResponseCount && 'o-40'">
                        <NavIcons :icon="item.ResponseCount ? 'check' : 'close'" :size="14" :strokeWidth="3"></NavIcons>
                        {{ $Resources.SurveyAnsweredLabel }}
                        {{ item.ResponseCount > 1 ? '(' + item.ResponseCount + ' ' + $Resources.SurveyAnsweredTimesText + ')' : '' }}
                    </span>

                    <span v-if="item.TypeName === 'EventParticipant'" class="d-flex flex-wrap align-items-center gap-xs fw-600 timeline-stats">
                        <NavIcons :icon="'event'" :size="14"></NavIcons>
                        {{ $Resources['EventGuestList' + item.EventParticipantStatusName + 'Label'] }}
                    </span>

                    <span v-if="item.TypeName === 'SmsRecipient'"
                          class="d-flex flex-wrap align-items-center gap-xs fw-600 timeline-stats"
                          :class="item.BounceCount && 'o-40'">
                        <NavIcons :icon="!item.BounceCount ? 'check' : 'close'" :size="14" :strokeWidth="3"></NavIcons>
                        {{ $Resources.ContactRecipientCountLabel }}
                    </span>
                </p>

                <h4 v-if="item.TypeName === 'ContactCreated'">
                    {{ $Resources.ContactCreatedLabel }}
                </h4>

                <h4 v-else>
                    <!--If ModifiedByUser has value the ObjectId references a User, not an Issue.
                        The URL will therefore incorrectly point to /issue/{userId}/statistics-->
                    <a :href="item.ModifiedByUser ? null : item.Url">
                        {{ item.Title || $Resources.NoTitlePlaceholder }}
                    </a>
                </h4>

                <p v-if="item.Description">{{ item.Description }}</p>

                <ul v-if="(item.TypeName == 'EmailBounce' || item.TypeName == 'SmsBounce') && (item.BounceCategory || item.BounceDiagnosis)" class="list-unstyled fa-ul">
                    <li>
                        <i class="fa-li fa fa-warning c-bounced" aria-hidden="true"></i>
                        <p>
                            <strong>
                                <span class="text-uppercase-first d-inline-block">{{ item.BounceCategory }}</span>
                                <span v-show="item.HardBounce">({{ $Resources.HardBounce }})</span>
                                <span v-show="!item.HardBounce">({{ $Resources.SoftBounce }})</span>
                            </strong>
                            {{ item.BounceDiagnosis }}
                        </p>
                    </li>
                </ul>

                <ul v-if="(item.TypeName == 'EmailUnbounced' || item.TypeName == 'SmsUnbounced') && (item.Data)" class="list-unstyled">
                    <li>
                        <p>
                            {{ item.Data }}
                        </p>
                    </li>
                </ul>

                <p v-if="item.ModifiedByUser" class="meta">
                    <span class="glyphicon glyphicon-user"></span>
                    <span>{{ $Resources.ModifiedByLabel}}: {{ item.ModifiedByUser.Name && item.ModifiedByUser.Name.trim() ? item.ModifiedByUser.Name : item.ModifiedByUser.UserName }}</span>
                </p>
            </div>
        </template>
    </TimelineList>
</template>

<style></style>
